/**
 * Redesigned Project List Page (Specra v2)
 *
 * Feature-flagged behind `specra_redesign_projects`.
 * Type filter pills, leaner card grid, right rail with quick-start / templates / recently closed.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SectionHeader, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Archive, Crosshair, FileText, FolderOpen, Layers, LayoutGrid, Search, Table as TableIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjectTypeLabel } from '../../constants/project-types';
import { useProjects } from '../../hooks/useProjects';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { cn } from '../../lib/utils';
import { getProjectPath } from '../../lib/utils/project-routes';
import { EntityTable } from '../../pages/Delivery/_shared/components/EntityTable';
import { compareByDateDesc, staleness } from '../../utils/time';
import { ErrorState, LoadingState, PageBody, PageHero } from '../layout';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ProjectActionsMenu } from './ProjectActionsMenu';
import { ProjectCardRedesign } from './ProjectCardRedesign';
import { matchesTypeFilter } from './projectFilters';
// Entry points for the "Create a project" box — each opens the create dialog
// pre-set to that project type. `sub` gives each type meaning at a glance.
const CREATE_OPTIONS = [
    {
        label: 'Pursuit',
        sub: 'Shape a deal thread — workstreams, touchpoints, go/no-go',
        icon: Crosshair,
        href: '/capture'
    },
    {
        label: 'Government Proposal',
        sub: 'Upload an RFP to extract requirements',
        icon: FileText,
        type: 'proposal'
    },
    {
        label: 'Contract Management',
        sub: 'Track CLINs, deliverables & obligations',
        icon: Layers,
        type: 'contract'
    },
    {
        label: 'Blank Project',
        sub: 'Start empty and add files later — not for pipeline work',
        icon: FolderOpen,
        type: 'general'
    }
];
const TYPE_BADGE_VARIANT = {
    proposal: 'info',
    contract: 'success',
    general: 'neutral',
    pre_award: 'warning'
};
// Columns for the table view. Ordering/filtering is driven by the page toolbar
// (search + type pills + status + sort), so these are display-only.
const LIST_COLUMNS = [
    {
        key: 'name',
        header: 'Name',
        render: (p)=>/*#__PURE__*/ _jsx("span", {
                className: "font-medium text-foreground",
                children: p.name
            })
    },
    {
        key: 'type',
        header: 'Type',
        render: (p)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: TYPE_BADGE_VARIANT[p.type] ?? 'neutral',
                children: getProjectTypeLabel(p.type)
            })
    },
    {
        key: 'memberCount',
        header: 'Members',
        align: 'right',
        render: (p)=>p.memberCount
    },
    {
        key: 'lastActivityAt',
        header: 'Last activity',
        render: (p)=>{
            const activity = staleness(p.lastActivityAt);
            return /*#__PURE__*/ _jsx("span", {
                className: activity.stale ? 'text-warning' : undefined,
                children: activity.label
            });
        }
    }
];
export function ProjectListRedesign() {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('active');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createType, setCreateType] = useState('general');
    // Persisted card/table view choice (survives reloads). Only `view` is used
    // here — sort/filter stay owned by the toolbar so both views stay in sync.
    const { view, setView } = useTablePreferences('ts:projects:list', {
        view: 'tile'
    });
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader
    ]);
    const { data, isLoading, error, refetch } = useProjects({
        status: 'all'
    });
    const { activeProjects, archivedProjects, filteredProjects, proposalCount, contractCount, recentlyClosed } = useMemo(()=>{
        const all = data?.projects ?? [];
        const active = all.filter((p)=>p.status === 'active');
        const archived = all.filter((p)=>p.status === 'archived');
        const statusFiltered = statusFilter === 'active' ? active : archived;
        // Type filter
        const typeFiltered = statusFiltered.filter((p)=>matchesTypeFilter(p.type, typeFilter));
        // Search
        const q = searchQuery.toLowerCase().trim();
        const filtered = q ? typeFiltered.filter((p)=>p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false)) : typeFiltered;
        // Sort
        const sorted = [
            ...filtered
        ].sort((a, b)=>{
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            return compareByDateDesc(a.lastActivityAt, b.lastActivityAt);
        });
        // Counts for pills (against the current status group, ignoring search)
        const proposals = statusFiltered.filter((p)=>p.type === 'proposal' || p.type === 'pre_award').length;
        const contracts = statusFiltered.filter((p)=>p.type === 'contract').length;
        // Recently closed: first 3 archived projects sorted by last activity
        const closed = [
            ...archived
        ].sort((a, b)=>compareByDateDesc(a.lastActivityAt, b.lastActivityAt)).slice(0, 3);
        return {
            activeProjects: active,
            archivedProjects: archived,
            filteredProjects: sorted,
            proposalCount: proposals,
            contractCount: contracts,
            recentlyClosed: closed
        };
    }, [
        data?.projects,
        statusFilter,
        typeFilter,
        searchQuery,
        sortBy
    ]);
    const openCreateDialog = (projectType = 'general')=>{
        setCreateType(projectType);
        setCreateDialogOpen(true);
    };
    // Loading
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsx(PageHero, {
                        title: "Projects",
                        subtitle: "Organize related files and chats together"
                    }),
                    /*#__PURE__*/ _jsx(LoadingState, {
                        rows: 3
                    })
                ]
            })
        });
    }
    // Error
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsx(PageHero, {
                        title: "Projects",
                        subtitle: "Organize related files and chats together"
                    }),
                    /*#__PURE__*/ _jsx(ErrorState, {
                        message: "Failed to load projects. Please try refreshing the page.",
                        onRetry: refetch
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsx(PageHero, {
                        title: "Projects",
                        subtitle: "Organize related files and chats together"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-8",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-5 flex items-center gap-4",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "relative flex-1 max-w-xs",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Search, {
                                                        className: "absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"
                                                    }),
                                                    /*#__PURE__*/ _jsx("input", {
                                                        type: "text",
                                                        value: searchQuery,
                                                        onChange: (e)=>setSearchQuery(e.target.value),
                                                        placeholder: "Search projects...",
                                                        className: "h-8 w-full rounded-md border border-border bg-card pl-8 pr-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>setSortBy((s)=>s === 'recent' ? 'name' : 'recent'),
                                                children: sortBy === 'recent' ? 'Sort: Recent \u25BE' : 'Sort: A-Z \u25BE'
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3 text-[13px]",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        onClick: ()=>setStatusFilter('active'),
                                                        className: `font-medium transition-colors ${statusFilter === 'active' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`,
                                                        children: [
                                                            "Active",
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "ml-1 text-xs text-muted-foreground",
                                                                children: activeProjects.length
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-border-strong",
                                                        children: "/"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        onClick: ()=>setStatusFilter('archived'),
                                                        className: `font-medium transition-colors ${statusFilter === 'archived' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`,
                                                        children: [
                                                            "Archived",
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "ml-1 text-xs text-muted-foreground",
                                                                children: archivedProjects.length
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "ml-auto inline-flex overflow-hidden rounded-md border border-border",
                                                children: [
                                                    /*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        "aria-label": "Card view",
                                                        "aria-pressed": view === 'tile',
                                                        "data-testid": Testids.project.listViewToggleTile,
                                                        onClick: ()=>setView('tile'),
                                                        className: cn('flex items-center px-2 py-1.5 transition-colors', view === 'tile' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'),
                                                        children: /*#__PURE__*/ _jsx(LayoutGrid, {
                                                            className: "size-3.5"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        "aria-label": "Table view",
                                                        "aria-pressed": view === 'table',
                                                        "data-testid": Testids.project.listViewToggleTable,
                                                        onClick: ()=>setView('table'),
                                                        className: cn('flex items-center border-l border-border px-2 py-1.5 transition-colors', view === 'table' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'),
                                                        children: /*#__PURE__*/ _jsx(TableIcon, {
                                                            className: "size-3.5"
                                                        })
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-5 flex flex-wrap items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(TypePill, {
                                                active: typeFilter === 'all',
                                                onClick: ()=>setTypeFilter('all'),
                                                count: statusFilter === 'active' ? activeProjects.length : archivedProjects.length,
                                                children: "All"
                                            }),
                                            /*#__PURE__*/ _jsx(TypePill, {
                                                active: typeFilter === 'proposal',
                                                onClick: ()=>setTypeFilter('proposal'),
                                                count: proposalCount,
                                                children: "Proposals"
                                            }),
                                            /*#__PURE__*/ _jsx(TypePill, {
                                                active: typeFilter === 'contract',
                                                onClick: ()=>setTypeFilter('contract'),
                                                count: contractCount,
                                                children: "Contracts"
                                            })
                                        ]
                                    }),
                                    filteredProjects.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                        className: "py-16 text-center",
                                        children: [
                                            /*#__PURE__*/ _jsx(FolderOpen, {
                                                className: "mx-auto mb-3 h-10 w-10 text-muted-foreground/50"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm font-medium text-foreground",
                                                children: searchQuery ? 'No matching projects' : statusFilter === 'active' ? 'No active projects' : 'No archived projects'
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mt-1 text-xs text-muted-foreground",
                                                children: searchQuery ? 'Try adjusting your search terms' : statusFilter === 'archived' ? 'Projects you archive will appear here' : 'Create a new project to get started'
                                            })
                                        ]
                                    }) : view === 'table' ? /*#__PURE__*/ _jsx(EntityTable, {
                                        data: filteredProjects,
                                        columns: LIST_COLUMNS,
                                        testid: Testids.project.listTable,
                                        rowTestid: Testids.project.listRow,
                                        rowKey: (p)=>p.projectId,
                                        onRowClick: (p)=>navigate(getProjectPath(p)),
                                        rowActions: (p)=>/*#__PURE__*/ _jsx(ProjectActionsMenu, {
                                                project: p
                                            })
                                    }) : /*#__PURE__*/ _jsx("div", {
                                        className: "grid gap-3 sm:grid-cols-2 xl:grid-cols-3",
                                        children: filteredProjects.map((project)=>/*#__PURE__*/ _jsx(ProjectCardRedesign, {
                                                project: project
                                            }, project.projectId))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("aside", {
                                className: "hidden lg:block w-[320px] shrink-0 space-y-5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(SectionHeader, {
                                                title: "Create a project"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraCard, {
                                                children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                                                    className: "space-y-1 p-2",
                                                    children: CREATE_OPTIONS.map((opt)=>/*#__PURE__*/ _jsxs("button", {
                                                            type: "button",
                                                            onClick: ()=>{
                                                                if ('href' in opt && opt.href) {
                                                                    navigate(opt.href);
                                                                    return;
                                                                }
                                                                if ('type' in opt && opt.type) openCreateDialog(opt.type);
                                                            },
                                                            "data-testid": 'href' in opt && opt.href ? Testids.project.pursuitCta : 'type' in opt && opt.type === 'general' ? Testids.project.newProjectButton : undefined,
                                                            className: "flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-left hover:bg-accent transition-colors",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(opt.icon, {
                                                                    className: "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                                                                }),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "min-w-0",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "block text-[13px] text-foreground",
                                                                            children: opt.label
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "block text-[11px] text-muted-foreground",
                                                                            children: opt.sub
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        }, opt.label))
                                                })
                                            })
                                        ]
                                    }),
                                    recentlyClosed.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(SectionHeader, {
                                                title: "Recently Closed"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraCard, {
                                                children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                                                    className: "space-y-1 p-2",
                                                    children: recentlyClosed.map((p)=>/*#__PURE__*/ _jsx(RecentlyClosedRow, {
                                                            project: p
                                                        }, p.projectId))
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CreateProjectDialog, {
                open: createDialogOpen,
                onOpenChange: setCreateDialogOpen,
                initialType: createType
            })
        ]
    });
}
/* ---- Local sub-components ---- */ function TypePill({ active, onClick, count, children }) {
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: onClick,
        className: `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-medium transition-colors ${active ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-border-strong'}`,
        children: [
            children,
            /*#__PURE__*/ _jsx("span", {
                className: `text-[11px] ${active ? 'text-background/70' : 'text-muted-foreground'}`,
                children: count
            })
        ]
    });
}
function RecentlyClosedRow({ project }) {
    return /*#__PURE__*/ _jsxs(Link, {
        to: getProjectPath(project),
        className: "flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] hover:bg-accent transition-colors",
        children: [
            /*#__PURE__*/ _jsx(Archive, {
                className: "w-3.5 h-3.5 text-muted-foreground shrink-0"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "truncate text-foreground",
                children: project.name
            }),
            /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                className: "ml-auto shrink-0",
                children: getProjectTypeLabel(project.type)
            })
        ]
    });
}
